import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileWarning, Loader2 } from 'lucide-react';
import { api, apiError } from '../api/client';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import FormField from '../components/FormField';
import LoadingSpinner from '../components/LoadingSpinner';

interface InutilizacaoNFe {
  id: number;
  emitente_id: number;
  ambiente: 'homologacao' | 'producao';
  ano: number;
  modelo: string;
  serie: number;
  numero_ini: number;
  numero_fim: number;
  justificativa: string;
  status: 'pendente' | 'homologada' | 'rejeitada' | 'erro';
  protocolo: string | null;
  c_stat_sefaz: string | null;
  x_motivo_sefaz: string | null;
  data_envio: string | null;
  data_homologacao: string | null;
  created_at: string | null;
}

async function listInutilizacoes(emitenteId: number): Promise<InutilizacaoNFe[]> {
  const { data } = await api.get<InutilizacaoNFe[]>(`/nfe/inutilizacoes`, {
    params: { emitente_id: emitenteId },
  });
  return data;
}

interface NovaInutilizacaoPayload {
  emitente_id: number;
  ano: number;
  serie: number;
  numero_ini: number;
  numero_fim: number;
  justificativa: string;
  modelo?: string;
}

async function criarInutilizacao(p: NovaInutilizacaoPayload): Promise<InutilizacaoNFe & { sucesso: boolean }> {
  const { data } = await api.post('/nfe/inutilizacoes', p);
  return data;
}

function StatusBadge({ status }: { status: InutilizacaoNFe['status'] }) {
  const map: Record<InutilizacaoNFe['status'], { label: string; cls: string; icon: typeof CheckCircle2 }> = {
    homologada: { label: 'Homologada', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30', icon: CheckCircle2 },
    rejeitada: { label: 'Rejeitada', cls: 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30', icon: AlertTriangle },
    erro: { label: 'Erro', cls: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30', icon: AlertTriangle },
    pendente: { label: 'Pendente', cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-neutral-500/10 dark:text-neutral-400 dark:border-neutral-500/30', icon: Loader2 },
  };
  const { label, cls, icon: Icon } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

export default function InutilizacoesPage() {
  const { emitenteId } = useEmitenteAtivo();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [serie, setSerie] = useState('1');
  const [numIni, setNumIni] = useState('');
  const [numFim, setNumFim] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null);

  useEffect(() => {
    const qpNumero = searchParams.get('numero');
    const qpSerie = searchParams.get('serie');
    const qpAno = searchParams.get('ano');
    if (qpNumero) { setNumIni(qpNumero); setNumFim(qpNumero); }
    if (qpSerie) setSerie(qpSerie);
    if (qpAno) setAno(qpAno);
    if (qpNumero || qpSerie || qpAno) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const q = useQuery({
    enabled: !!emitenteId,
    queryKey: ['inutilizacoes', emitenteId],
    queryFn: () => listInutilizacoes(emitenteId!),
    refetchOnWindowFocus: false,
  });

  const mut = useMutation({
    mutationFn: criarInutilizacao,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['inutilizacoes', emitenteId] });
      if (data.status === 'homologada') {
        setFeedback({ tipo: 'ok', msg: `Inutilização homologada. Protocolo ${data.protocolo ?? '-'}, cStat ${data.c_stat_sefaz}` });
        setNumIni('');
        setNumFim('');
        setJustificativa('');
      } else {
        setFeedback({
          tipo: 'err',
          msg: `${data.status.toUpperCase()}: cStat ${data.c_stat_sefaz ?? '-'}, ${data.x_motivo_sefaz ?? 'sem detalhe'}`,
        });
      }
    },
    onError: (e) => {
      setFeedback({ tipo: 'err', msg: apiError(e, 'Falha na inutilização') });
    },
  });

  const formValido = useMemo(() => {
    if (!emitenteId) return false;
    const a = Number(ano), s = Number(serie), i = Number(numIni), f = Number(numFim);
    if (!a || a < 2000 || a > 9999) return false;
    if (s < 0) return false;
    if (!i || !f || f < i) return false;
    if (justificativa.trim().length < 15) return false;
    return true;
  }, [emitenteId, ano, serie, numIni, numFim, justificativa]);

  const jLen = justificativa.trim().length;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    if (!formValido || !emitenteId) return;
    if (!confirm(`Confirma inutilização NF-e nº ${numIni}${numIni !== numFim ? ` a ${numFim}` : ''} série ${serie}? Esta ação é definitiva na SEFAZ.`)) return;
    mut.mutate({
      emitente_id: emitenteId,
      ano: Number(ano),
      serie: Number(serie),
      numero_ini: Number(numIni),
      numero_fim: Number(numFim),
      justificativa: justificativa.trim(),
    });
  }

  if (!emitenteId) {
    return (
      <div className="card-surface p-6 text-center text-sm text-neutral-600 dark:text-neutral-400">
        Selecione um emitente para gerenciar inutilizações.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl md:text-2xl font-semibold">Inutilização de numeração NF-e</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Envie pedidos de inutilização de numeração para a SEFAZ. Use em caso de NF-e rejeitada em que a numeração já foi consumida.
        </p>
      </header>

      <section className="card-surface p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FileWarning className="h-5 w-5 text-amber-700 dark:text-amber-400" />
          <h2 className="font-semibold">Novo pedido de inutilização</h2>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FormField label="Ano" type="number" value={ano} onChange={(e) => setAno(e.target.value)} hint="AAAA" required />
            <FormField label="Série" type="number" value={serie} onChange={(e) => setSerie(e.target.value)} required />
            <FormField
              label="Número inicial"
              type="number"
              value={numIni}
              onChange={(e) => {
                setNumIni(e.target.value);
                if (!numFim) setNumFim(e.target.value);
              }}
              required
            />
            <FormField
              label="Número final"
              type="number"
              value={numFim}
              onChange={(e) => setNumFim(e.target.value)}
              hint="Igual ao inicial se for só uma nota"
              required
            />
          </div>

          <div>
            <label className="field-label">
              Justificativa <span className="text-rose-700 dark:text-rose-400">*</span>
            </label>
            <textarea
              className="field-input"
              rows={3}
              minLength={15}
              maxLength={255}
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              placeholder="Mínimo 15 caracteres. Ex: Erro de digitação no destinatário, número inutilizado."
              required
            />
            <p className={`text-xs mt-1 ${jLen < 15 ? 'text-rose-700 dark:text-rose-400' : 'text-neutral-500'}`}>
              {jLen}/255 {jLen < 15 && `(faltam ${15 - jLen} para o mínimo)`}
            </p>
          </div>

          {feedback && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                feedback.tipo === 'ok'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300'
                  : 'border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-300'
              }`}
            >
              {feedback.msg}
            </div>
          )}

          <div className="flex justify-end">
            <button type="submit" disabled={!formValido || mut.isPending} className="btn-primary">
              {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileWarning className="h-4 w-4" />}
              {mut.isPending ? 'Enviando à SEFAZ...' : 'Enviar inutilização'}
            </button>
          </div>
        </form>
      </section>

      <section className="card-surface p-5 space-y-3">
        <h2 className="font-semibold">Últimas inutilizações</h2>

        {q.isLoading && (
          <div className="py-6 grid place-items-center">
            <LoadingSpinner />
          </div>
        )}

        {!q.isLoading && (q.data?.length ?? 0) === 0 && (
          <p className="text-sm text-neutral-500 text-center py-6">
            Nenhuma inutilização registrada ainda.
          </p>
        )}

        {!q.isLoading && (q.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-slate-200 dark:border-white/10">
                <tr>
                  <th className="py-2 px-2">Data</th>
                  <th className="py-2 px-2">Série</th>
                  <th className="py-2 px-2">Faixa</th>
                  <th className="py-2 px-2">Justificativa</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Protocolo</th>
                  <th className="py-2 px-2">Motivo SEFAZ</th>
                  <th className="py-2 px-2">Arquivos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {q.data!.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                    <td className="py-2 px-2 whitespace-nowrap text-neutral-600 dark:text-neutral-400">
                      {r.data_envio ? new Date(r.data_envio).toLocaleString('pt-BR') : '-'}
                    </td>
                    <td className="py-2 px-2">{r.serie}</td>
                    <td className="py-2 px-2 mono">
                      {r.numero_ini === r.numero_fim ? r.numero_ini : `${r.numero_ini}-${r.numero_fim}`}
                    </td>
                    <td className="py-2 px-2 max-w-xs truncate" title={r.justificativa}>
                      {r.justificativa}
                    </td>
                    <td className="py-2 px-2">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="py-2 px-2 mono text-xs text-neutral-600 dark:text-neutral-400">{r.protocolo || '-'}</td>
                    <td className="py-2 px-2 text-xs text-neutral-600 dark:text-neutral-400 max-w-[240px] truncate" title={r.x_motivo_sefaz || ''}>
                      {r.c_stat_sefaz ? `${r.c_stat_sefaz}, ` : ''}{r.x_motivo_sefaz || '-'}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {r.status === 'homologada' ? (
                        <div className="flex gap-1">
                          <a href={`/api/nfe/inutilizacoes/${r.id}/pdf`} target="_blank" rel="noreferrer" className="btn-sky text-[10px] !py-0.5 !px-1.5">PDF</a>
                          <a href={`/api/nfe/inutilizacoes/${r.id}/xml`} target="_blank" rel="noreferrer" className="toolbar-btn text-[10px] !py-0.5 !px-1.5">XML</a>
                        </div>
                      ) : <span className="text-neutral-600 text-xs">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
